import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './Page11.css';

function Page11() {
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [recordings, setRecordings] = useState([]);
  const [isLoadingDates, setIsLoadingDates] = useState(true);
  const [isLoadingRecordings, setIsLoadingRecordings] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [currentRecording, setCurrentRecording] = useState(null);
  const [error, setError] = useState('');
  const [selectedRecording, setSelectedRecording] = useState(null); // For modal
  const [audioProgress, setAudioProgress] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);
  const [deviceFilter, setDeviceFilter] = useState('all');
  const [showAll, setShowAll] = useState(false);
  const audioRef = useRef(null);

  // Fetch available dates on mount
  useEffect(() => {
    fetchDates();
  }, []);

  // Fetch recordings when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchRecordings(selectedDate);
    }
  }, [selectedDate]);

  const fetchDates = async () => {
    setIsLoadingDates(true);
    try {
      const response = await axios.get(
        'https://kidr78home.duckdns.org/api/audio_archive/dates',
        { headers: { Authorization: 'kSuIlUmNaTiIeO5' } }
      );
      setDates(response.data.dates || []);
      if (response.data.dates && response.data.dates.length > 0) {
        setSelectedDate(response.data.dates[0]);
      }
    } catch (err) {
      console.error('Error fetching dates:', err);
      setError('Failed to load archive dates');
    } finally {
      setIsLoadingDates(false);
    }
  };

  const fetchRecordings = async (date) => {
    setIsLoadingRecordings(true);
    setError('');
    try {
      const response = await axios.get(
        `https://kidr78home.duckdns.org/api/audio_archive/list/${date}`,
        { headers: { Authorization: 'kSuIlUmNaTiIeO5' } }
      );
      setRecordings(response.data.recordings || []);
    } catch (err) {
      console.error('Error fetching recordings:', err);
      setError('Failed to load recordings');
      setRecordings([]);
    } finally {
      setIsLoadingRecordings(false);
    }
  };


  const playAudio = async (recording, e) => {
    // Prevent card click when clicking play button
    if (e) e.stopPropagation();
    
    if (currentlyPlaying === recording.filename) {
      // Toggle pause/play
      if (audioRef.current.paused) {
        audioRef.current.play();
      } else {
        audioRef.current.pause();
      }
      return;
    }

    // Fetch audio with auth header and create blob URL
    try {
      setCurrentlyPlaying(recording.filename);
      setCurrentRecording(recording);
      const audioUrl = `https://kidr78home.duckdns.org/api/audio_archive/play/${recording.date}/${recording.filename}`;
      
      const response = await fetch(audioUrl, {
        headers: { Authorization: 'kSuIlUmNaTiIeO5' }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch audio');
      }
      
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      if (audioRef.current) {
        // Revoke previous blob URL to free memory
        if (audioRef.current.src.startsWith('blob:')) {
          URL.revokeObjectURL(audioRef.current.src);
        }
        audioRef.current.src = blobUrl;
        audioRef.current.load();
        audioRef.current.play().catch(err => {
          console.error('Error playing audio:', err);
          setError('Failed to play audio');
          setCurrentlyPlaying(null);
          setCurrentRecording(null);
        });
      }
    } catch (err) {
      console.error('Error fetching audio:', err);
      setError('Failed to load audio file');
      setCurrentlyPlaying(null);
      setCurrentRecording(null);
    }
  };

  // Audio control functions
  const seekAudio = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(audioRef.current.duration, audioRef.current.currentTime + seconds));
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setAudioProgress(audioRef.current.currentTime);
      setAudioDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeekBar = (e) => {
    if (audioRef.current && audioDuration) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      audioRef.current.currentTime = percent * audioDuration;
    }
  };

  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
    setCurrentRecording(null);
    setAudioProgress(0);
  };

  const formatPlayTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '--';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatTime = (filename) => {
    // Extract time from filename like ears1_capture_2026-01-31_11-50-00.wav
    const match = filename.match(/_(\d{2})-(\d{2})-(\d{2})\.wav$/);
    if (match) {
      return `${match[1]}:${match[2]}:${match[3]}`;
    }
    return filename;
  };

  const getDeviceId = (filename) => {
    // Extract device prefix from filenames like ears1_capture_... or ears2_capture_...
    const match = filename.match(/^(.+?)_capture_/);
    return match ? match[1] : null;
  };

  const deviceColors = {
    ears1: '#3498db',
    ears2: '#e67e22',
    ears3: '#2ecc71',
    ears4: '#e74c3c',
  };

  const getLanguageFlag = (lang) => {
    const flags = {
      'ru': '🇷🇺',
      'en': '🇬🇧',
      'fi': '🇫🇮',
      'de': '🇩🇪',
      'fr': '🇫🇷',
      'es': '🇪🇸',
    };
    return flags[lang] || '🌐';
  };

  const getClassificationIcon = (classification) => {
    const icons = {
      'speech': '🗣️',
      'silence': '🔇',
      'music': '🎵',
      'noise': '📢',
      'door': '🚪',
      'knock': '👊',
      'dog': '🐕',
      'cat': '🐱',
      'alarm': '🚨',
      'vehicle': '🚗',
    };
    if (!classification) return '🔊';
    const lower = classification.toLowerCase();
    for (const [key, icon] of Object.entries(icons)) {
      if (lower.includes(key)) return icon;
    }
    return '🔊';
  };

  const getYamnetIcon = (label) => {
    const lower = label.toLowerCase();
    if (lower.includes('speech') || lower.includes('conversation')) return '🗣️';
    if (lower.includes('silence')) return '🔇';
    if (lower.includes('music')) return '🎵';
    if (lower.includes('dog')) return '🐕';
    if (lower.includes('cat')) return '🐱';
    if (lower.includes('door')) return '🚪';
    if (lower.includes('knock')) return '👊';
    if (lower.includes('alarm') || lower.includes('siren')) return '🚨';
    if (lower.includes('vehicle') || lower.includes('car')) return '🚗';
    if (lower.includes('bird')) return '🐦';
    if (lower.includes('water')) return '💧';
    if (lower.includes('wind')) return '💨';
    if (lower.includes('rain')) return '🌧️';
    if (lower.includes('breathing') || lower.includes('snore')) return '😮‍💨';
    if (lower.includes('sneeze') || lower.includes('cough')) return '🤧';
    if (lower.includes('laugh')) return '😄';
    if (lower.includes('cry')) return '😢';
    if (lower.includes('room') || lower.includes('inside')) return '🏠';
    return '🔊';
  };

  // Derive available devices from current recordings
  const availableDevices = [...new Set(recordings.map(r => getDeviceId(r.filename)).filter(Boolean))];

  const filteredRecordings = (deviceFilter === 'all'
    ? recordings
    : recordings.filter(r => getDeviceId(r.filename) === deviceFilter)
  ).filter(r => {
    if (showAll) return true;
    const cls = (r.classification || '').toLowerCase();
    return cls.includes('speech') || cls.includes('human');
  }).sort((a, b) => {
    // Sort by timestamp in filename so ears1 and ears2 interleave chronologically
    const timeA = a.filename.match(/_(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})\.wav$/);
    const timeB = b.filename.match(/_(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})\.wav$/);
    return (timeB?.[1] || '').localeCompare(timeA?.[1] || '');
  });

  return (
    <div className="archive-page">
      {/* Hidden audio element */}
      <audio 
        ref={audioRef} 
        onEnded={handleAudioEnded}
        onTimeUpdate={handleTimeUpdate}
        onError={() => setError('Audio playback error')}
      />

      {/* Detail Modal */}
      {selectedRecording && (
        <div className="modal-backdrop" onClick={() => setSelectedRecording(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📝 Recording Details</h2>
              <button className="modal-close" onClick={() => setSelectedRecording(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <span className="detail-label">🕐 Time:</span>
                <span className="detail-value">{formatTime(selectedRecording.filename)}</span>
              </div>
              {getDeviceId(selectedRecording.filename) && (
                <div className="detail-row">
                  <span className="detail-label">📡 Device:</span>
                  <span className="detail-value">
                    <span 
                      className="device-badge"
                      style={{ background: deviceColors[getDeviceId(selectedRecording.filename)] || '#95a5a6' }}
                    >
                      {getDeviceId(selectedRecording.filename)}
                    </span>
                  </span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">⏱️ Duration:</span>
                <span className="detail-value">{formatDuration(selectedRecording.duration_s)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">🗣️ Speech:</span>
                <span className="detail-value">{selectedRecording.has_speech ? 'Yes' : 'No'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">🌐 Language:</span>
                <span className="detail-value">{selectedRecording.lang ? `${getLanguageFlag(selectedRecording.lang)} ${selectedRecording.lang.toUpperCase()}` : 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">📊 VAD Ratio:</span>
                <span className="detail-value">{selectedRecording.vad_ratio ? `${(selectedRecording.vad_ratio * 100).toFixed(1)}%` : 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">🔊 RMS:</span>
                <span className="detail-value">{selectedRecording.rms ? selectedRecording.rms.toFixed(3) : 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">📋 Decision:</span>
                <span className="detail-value">{selectedRecording.decision || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">💡 Reason:</span>
                <span className="detail-value">{selectedRecording.reason || 'N/A'}</span>
              </div>
              {selectedRecording.classification && (
                <div className="detail-row">
                  <span className="detail-label">🏷️ Classification:</span>
                  <span className="detail-value">{getClassificationIcon(selectedRecording.classification)} {selectedRecording.classification}</span>
                </div>
              )}
              {/* YAMNet Analysis Section */}
              {selectedRecording.yamnet_top && selectedRecording.yamnet_top.length > 0 && (
                <div className="yamnet-section">
                  <span className="detail-label">🧠 YAMNet Analysis:</span>
                  <div className="yamnet-bars">
                    {selectedRecording.yamnet_top.map(([label, score], i) => (
                      <div key={i} className="yamnet-bar-row">
                        <span className="yamnet-label">{getYamnetIcon(label)} {label}</span>
                        <div className="yamnet-bar-container">
                          <div 
                            className="yamnet-bar" 
                            style={{ width: `${score * 100}%` }}
                          />
                        </div>
                        <span className="yamnet-score">{(score * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {(selectedRecording.transcript || selectedRecording.transcript_preview) && (
                <div className="transcript-section">
                  <span className="detail-label">📜 Full Transcript:</span>
                  <p className="full-transcript">{selectedRecording.transcript || selectedRecording.transcript_preview}</p>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="play-modal-btn" onClick={(e) => { playAudio(selectedRecording, e); }}>
                {currentlyPlaying === selectedRecording.filename ? '⏸️ Pause' : '▶️ Play'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Now Playing Bar */}
      {currentRecording && (
        <div className="now-playing-bar">
          <div className="now-playing-info">
            <span className="now-playing-title">
              🎵 {formatTime(currentRecording.filename)}
              {getDeviceId(currentRecording.filename) && (
                <span 
                  className="device-badge device-badge-sm"
                  style={{ background: deviceColors[getDeviceId(currentRecording.filename)] || '#95a5a6', marginLeft: '0.5rem' }}
                >
                  {getDeviceId(currentRecording.filename)}
                </span>
              )}
            </span>
            <span className="now-playing-time">{formatPlayTime(audioProgress)} / {formatPlayTime(audioDuration)}</span>
          </div>
          <div className="player-controls">
            <button className="control-btn" onClick={() => seekAudio(-10)}>⏪ 10s</button>
            <button className="control-btn play-pause" onClick={(e) => playAudio(currentRecording, e)}>
              {audioRef.current?.paused ? '▶️' : '⏸️'}
            </button>
            <button className="control-btn" onClick={() => seekAudio(10)}>10s ⏩</button>
          </div>
          <div className="seek-bar" onClick={handleSeekBar}>
            <div className="seek-progress" style={{ width: `${audioDuration ? (audioProgress / audioDuration) * 100 : 0}%` }}></div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="archive-header">
        <h1 className="page-title">🎵 Audio Archive</h1>
        <p className="page-subtitle">Browse and play recorded audio captures</p>
      </div>

      {/* Date Selector */}
      <div className="date-selector-section">
        <div className="date-selector-card">
          <label htmlFor="date-select" className="date-label">📅 Select Date</label>
          {isLoadingDates ? (
            <div className="loading-text">Loading dates...</div>
          ) : (
            <select
              id="date-select"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="date-select"
            >
              {dates.map(date => (
                <option key={date} value={date}>{date}</option>
              ))}
            </select>
          )}
          {selectedDate && (
            <div className="recording-count">
              {isLoadingRecordings ? 'Loading...' : `${filteredRecordings.length} recordings`}
            </div>
          )}
          {availableDevices.length > 1 && (
            <>
              <label htmlFor="device-filter" className="date-label">📡 Device</label>
              <select
                id="device-filter"
                value={deviceFilter}
                onChange={(e) => setDeviceFilter(e.target.value)}
                className="date-select"
                style={{ minWidth: '120px' }}
              >
                <option value="all">All ({recordings.length})</option>
                {availableDevices.map(d => (
                  <option key={d} value={d}>
                    {d} ({recordings.filter(r => getDeviceId(r.filename) === d).length})
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            className={`show-all-btn ${showAll ? 'active' : ''}`}
            onClick={() => setShowAll(prev => !prev)}
          >
            {showAll ? '🔊 All sounds' : '🗣️ Speech only'}
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-toast">
          <span>❌ {error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Recordings List */}
      <div className="recordings-section">
        {isLoadingRecordings ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading recordings...</p>
          </div>
        ) : filteredRecordings.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">📭</span>
            <p>No recordings found{deviceFilter !== 'all' ? ` for ${deviceFilter}` : ' for this date'}</p>
          </div>
        ) : (
          <div className="recordings-list">
            {filteredRecordings.map((recording) => (
              <div 
                key={recording.filename} 
                className={`recording-card ${currentlyPlaying === recording.filename ? 'playing' : ''}`}
                onClick={() => setSelectedRecording(recording)}
              >
                <div className="recording-header">
                  <div className="recording-time">
                    <span className="time-icon">🕐</span>
                    <span className="time-text">{formatTime(recording.filename)}</span>
                  </div>
                  <div className="recording-meta">
                    {getDeviceId(recording.filename) && (
                      <span 
                        className="device-badge"
                        style={{ background: deviceColors[getDeviceId(recording.filename)] || '#95a5a6' }}
                      >
                        📡 {getDeviceId(recording.filename)}
                      </span>
                    )}
                    <span className="duration">{formatDuration(recording.duration_s)}</span>
                    {recording.classification && (
                      <span className="classification-badge">
                        {getClassificationIcon(recording.classification)} {recording.classification}
                      </span>
                    )}
                    {recording.lang && (
                      <span className="lang-badge">{getLanguageFlag(recording.lang)} {recording.lang.toUpperCase()}</span>
                    )}
                  </div>
                </div>

                <div className="recording-body">
                  <button 
                    className={`play-button ${currentlyPlaying === recording.filename ? 'playing' : ''}`}
                    onClick={(e) => playAudio(recording, e)}
                  >
                    {currentlyPlaying === recording.filename ? '⏸️' : '▶️'}
                  </button>
                  
                  <div className="recording-info">
                    {/* YAMNet summary on card */}
                    {recording.yamnet_top && recording.yamnet_top.length > 0 && (
                      <div className="yamnet-summary">
                        {recording.yamnet_top.slice(0, 3).map(([label, score], i) => (
                          <span key={i} className="yamnet-tag">
                            {getYamnetIcon(label)} {label} {(score * 100).toFixed(0)}%
                          </span>
                        ))}
                      </div>
                    )}
                    {(recording.transcript || recording.transcript_preview) && (
                      <p className="transcript">
                        "{recording.transcript || recording.transcript_preview}"
                      </p>
                    )}
                    <div className="recording-stats">
                      {recording.vad_ratio > 0 && (
                        <span className="stat">VAD: {(recording.vad_ratio * 100).toFixed(0)}%</span>
                      )}
                      {recording.rms > 0 && (
                        <span className="stat">RMS: {recording.rms.toFixed(2)}</span>
                      )}
                      {recording.reason && (
                        <span className="stat reason">{recording.reason}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Page11;
